import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    Button,
    TablePagination,
    Avatar,
    CircularProgress,
    useTheme,
    MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ResponsiveAppBar from '../../../../component/appbar';
import { GetBoatData, GetBoatWork, getSummaryBoatData, searchBoat, searchBoatsWork } from '../../../../service/boat_service';
import { MdPreview } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import Cookies from "js-cookie";
import { alpha } from '@mui/material/styles';
import { IoSettingsSharp, IoPlayBackCircle, IoBoat } from "react-icons/io5";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { FaPlay } from "react-icons/fa";
import { getTimeSlotsActive } from '../../../../service/utinity_service';

// Styled components for the table (unchanged)
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
}));

// New component for status display

const StatusCard = ({ title, count, icon, color, onClick }) => {
    const theme = useTheme();

    return (
        <Card
            sx={{
                backgroundColor: 'white',
                height: '100%',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[10],
                },
            }}
            onClick={onClick}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100%',
                    background: `linear-gradient(45deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
                    zIndex: 0,
                }}
            />
            <CardContent
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 3,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Avatar
                            sx={{
                                bgcolor: color,
                                width: 52,
                                height: 52,
                                boxShadow: `0 4px 20px ${alpha(color, 0.5)}`,
                            }}
                        >
                            {icon}
                        </Avatar>
                        <Typography
                            variant="h6"
                            marginTop={'18px'}
                            sx={{
                                fontWeight: 'medium',
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                        }}
                    >
                        {count}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const ShipInventoryDashboard = () => {
    const token = Cookies.get('token');
    const navigate = useNavigate();
    const [boatData, setBoatData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [boatWork, setBoatWork] = useState([]);
    const [statusData, setStatusData] = useState([]);
    const dataBoatFrom = {
        name: '',
        model: '',
        engine: '',
        status: '',
    }

    const [timeslots, setTimeslots] = useState([]);

    const dataBoatWorkFrom = {
        booking_id: '',
        date: '',
        time: '',
        boatname: '',
        butlerName: '',
        driverName: '',
    }

    const [boatFrom, setBoatFrom] = useState(dataBoatFrom)
    const [boatWorkFrom, setBoatWorkFrom] = useState(dataBoatWorkFrom)

    // Pagination states
    const [boatPage, setBoatPage] = useState(0);
    const [boatRowsPerPage, setBoatRowsPerPage] = useState(10);
    const [workPage, setWorkPage] = useState(0);
    const [workRowsPerPage, setWorkRowsPerPage] = useState(10);

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }

        handleGetBoatData();
        handleGetBoatWork();
        handleGetSummary();
        handleGetTimeSlot();
    }, []);

    const handleBoatFromChange = (event) => {
        const { name, value } = event.target;
        setBoatFrom(prevState => ({
            ...prevState,
            [name]: value
        }))
    };

    const handleGetTimeSlot = async () => {
        try {
            const res = await getTimeSlotsActive()
            setTimeslots(res)
        } catch (error) {
            navigate('/404')
        }
    };

    const handleBoatWorkFromChange = (event) => {
        const { name, value } = event.target;

        setBoatWorkFrom(prevState => ({
            ...prevState,
            [name]: value
        }))

    };

    const handleSearchWork = async (data) => {
        // console.log("boatworkfrom",boatWorkFrom)
        try {
            const res = await searchBoatsWork(data, token)
            setBoatWork(res)
        } catch (error) {
            console.error(error)
            setBoatWork([])
        }
    }

    const handleSearchBoat = async (data) => {
        // console.log("boatfrom",boatFrom)
        try {
            const res = await searchBoat(data, token)
            setBoatData(res)
        } catch (error) {
            console.error(error)
            setBoatData([])
        }
    }

    // Existing handler functions (unchanged)
    const handleGetBoatData = async () => {
        setLoading(true);
        try {
            const res = await GetBoatData();
            setBoatData(res);
            // console.log("GetBoatData : ", res);
            setError(null);
        } catch (e) {
            setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
        }
        setLoading(false);
    };

    const handleGetSummary = async () => {
        try {
            const res = await getSummaryBoatData(token);
            // console.log(res)
            const totalBoats = res.reduce((sum, item) => sum + item.boat_count, 0);
            setStatusData(res);
        } catch (error) {
            console.error(error)
            navigate('/404')
        }
    };

    const handleGetBoatWork = async () => {
        try {
            const res = await GetBoatWork(token);
            setBoatWork(res);
        } catch (e) {
            setError("เกิดข้อผิดพลาดในการโหลดข้อมูลการทำงาน");
        }
    };

    const getStatusCount = (status) => {
        const item = statusData.find(item => item.status === status);
        return item ? item.boat_count : 0;
    };

    const handleChangeBoatPage = (event, newPage) => {
        setBoatPage(newPage);
    };

    const handleChangeBoatRowsPerPage = (event) => {
        setBoatRowsPerPage(parseInt(event.target.value, 10));
        setBoatPage(0);
    };

    // Pagination handlers for boat work table
    const handleChangeWorkPage = (event, newPage) => {
        setWorkPage(newPage);
    };

    const handleChangeWorkRowsPerPage = (event) => {
        setWorkRowsPerPage(parseInt(event.target.value, 10));
        setWorkPage(0);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="90vh">
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="90vh">
                    <Typography color="error">{error}</Typography>
                </Box>
            );
        }

        // if (!boatData || boatData.length === 0) {
        //     return (
        //         <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        //             <Typography>ไม่พบข้อมูลเรือ</Typography>
        //         </Box>
        //     );
        // }

        return (
            <Grid container spacing={3}>
                {/* Status Cards */}
                <Grid container spacing={2} sx={{ marginTop: '4px' }}>
                    <Grid item xs={12} md={3}>
                        <StatusCard
                            title="จำนวนเรือทั้งหมด"
                            count={getStatusCount("ทั้งหมด")}
                            icon={<IoBoat />}
                            color="#3f51b5"
                            onClick={() => handleSearchBoat({
                                name: '',
                                model: '',
                                engine: '',
                                status: '',
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatusCard
                            title="กำลังใช้งาน"
                            count={getStatusCount("กำลังใช้งาน")}
                            icon={<FaPlay />}
                            color="#4caf50"
                            onClick={() => handleSearchBoat({
                                name: '',
                                model: '',
                                engine: '',
                                status: "กำลังใช้งาน"
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatusCard
                            title="ส่งซ่อม"
                            count={getStatusCount("ส่งซ่อม")}
                            icon={<IoSettingsSharp />}
                            color="#ff9800"
                            onClick={() => handleSearchBoat({
                                name: '',
                                model: '',
                                engine: '',
                                status: "ส่งซ่อม"
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatusCard
                            title="ยกเลิก"
                            count={getStatusCount("ยกเลิกการใช้งาน")}
                            icon={<RiDeleteBin5Fill />}
                            color="#f44336"
                            onClick={() => handleSearchBoat({
                                name: '',
                                model: '',
                                engine: '',
                                status: "ยกเลิก"
                            })}
                        />
                    </Grid>
                </Grid>

                {/* Boat Inventory Table */}
                <Grid item xs={12}>
                    <Card sx={{ padding: 3, marginTop: 1, marginBottom: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            ค้นหารายการเรือในคลัง
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>ชื่อเรือ</Typography>
                                <TextField
                                    name="name"
                                    label="ชื่อเรือ"
                                    value={boatFrom.name}
                                    onChange={handleBoatFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>โมเดล</Typography>
                                <TextField
                                    name="model"
                                    label="โมเดล"
                                    value={boatData.model}
                                    onChange={handleBoatFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>เครื่องยนต์</Typography>
                                <TextField
                                    name="engine"
                                    label="เครื่องยนต์"
                                    value={boatFrom.engine}
                                    onChange={handleBoatFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>สถานะเรือ</Typography>
                                <TextField
                                    select
                                    name="status"
                                    label="สถานะเรือ"
                                    value={boatFrom.status}
                                    onChange={handleBoatFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                >
                                    <MenuItem value="กำลังใช้งาน">กำลังใช้งาน</MenuItem>
                                    <MenuItem value="ส่งซ่อม">ส่งซ่อม</MenuItem>
                                    <MenuItem value="ยกเลิก">ยกเลิก</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="outlined" color="primary" sx={{ marginRight: 2, width: '200px' }} onClick={(e) => setBoatFrom(dataBoatFrom)}>
                                ล้างค่า
                            </Button>
                            <Button variant="contained" color="primary" sx={{ width: '200px' }} onClick={() => handleSearchBoat(boatFrom)}>
                                ค้นหา
                            </Button>
                        </Box>
                    </Card>

                    <Card sx={{ height: 'auto', overflow: '-moz-hidden-unscrollable' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <Typography variant="h6" gutterBottom>
                                    รายการเรือในคลัง
                                </Typography>
                                <Button variant='contained' onClick={() => navigate('/thewonderblueadmin_boat/create')} sx={{ width: '200px' }}>
                                    เพิ่มเรือ
                                </Button>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>ลำดับ</StyledTableCell>
                                            <StyledTableCell>ชื่อเรือ</StyledTableCell>
                                            <StyledTableCell>โมเดล</StyledTableCell>
                                            <StyledTableCell>เครื่องยนต์</StyledTableCell>
                                            <StyledTableCell>สี</StyledTableCell>
                                            <StyledTableCell>สีด้านใน</StyledTableCell>
                                            <StyledTableCell>สีท้องเรือ</StyledTableCell>
                                            <StyledTableCell>สถาณะ</StyledTableCell>
                                            <StyledTableCell>รายละเอียด</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boatData
                                            .slice(boatPage * boatRowsPerPage, boatPage * boatRowsPerPage + boatRowsPerPage)
                                            .map((row, index) => (
                                                <StyledTableRow key={row.id}>
                                                    <StyledTableCell>{boatPage * boatRowsPerPage + index + 1}</StyledTableCell>
                                                    <StyledTableCell>{row.name}</StyledTableCell>
                                                    <StyledTableCell>{row.model}</StyledTableCell>
                                                    <StyledTableCell>{row.engine}</StyledTableCell>
                                                    <StyledTableCell>{row.woodColor}</StyledTableCell>
                                                    <StyledTableCell>{row.interiorColor}</StyledTableCell>
                                                    <StyledTableCell>{row.hullColor}</StyledTableCell>
                                                    <StyledTableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.status}
                                                            color={
                                                                row.status === 'กำลังใช้งาน'
                                                                  ? 'success'
                                                                  : row.status === 'ส่งซ่อม'
                                                                    ? 'warning'
                                                                    : 'error'
                                                              }
                                                            sx={{ width: '160px' }}
                                                        />
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Button
                                                            size="medium"
                                                            onClick={() => navigate('/thewonderblueadmin_boat/' + row.id)}
                                                        >
                                                            <MdPreview fontSize={'24px'} color='gray' />
                                                        </Button>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25]}
                                component="div"
                                count={boatData.length}
                                rowsPerPage={boatRowsPerPage}
                                page={boatPage}
                                onPageChange={handleChangeBoatPage}
                                onRowsPerPageChange={handleChangeBoatRowsPerPage}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* <BoatScheduleTimeline /> */}

                {/* Boat Work Table */}
                <Grid item xs={12}>
                    <Card sx={{ padding: 3, marginTop: 1, marginBottom: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            ค้นหารายการเดินเรือ
                        </Typography>
                        <Grid container spacing={2} marginBottom={1}>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>หมายเลขตั๋ว</Typography>
                                <TextField
                                    name="booking_id"
                                    label="หมายเลขตั๋ว"
                                    value={boatWorkFrom.booking_id}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>ชื่อเรือ</Typography>
                                <TextField
                                    name="boatname"
                                    label="ชื่อเรือ"
                                    value={boatWorkFrom.boatname}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>วันที่เริ่มทำงาน (DD-MM-YYYY)</Typography>
                                <TextField
                                    name="date"
                                    label="วันที่เริ่มทำงาน"
                                    value={boatWorkFrom.date}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>เวลา</Typography>
                                <TextField
                                    select
                                    name="time"
                                    label="เวลา"
                                    value={boatWorkFrom.time}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200, // กำหนดความสูงสูงสุดของเมนู (เช่น 200px)
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {timeslots.length > 0 &&
                                        timeslots.map((status) => (
                                            <MenuItem key={status.time} value={status.time}>
                                                {status.time}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>พนักงานขับ</Typography>
                                <TextField
                                    name="driverName"
                                    label="พนักงานขับ"
                                    value={boatWorkFrom.driverName}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ marginBottom: 1, fontSize: '18px' }}>ผู้ช่วย</Typography>
                                <TextField
                                    name="butlerName"
                                    label="ผู้ช่วย"
                                    value={boatWorkFrom.butlerName}
                                    onChange={handleBoatWorkFromChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="outlined" color="primary" sx={{ marginRight: 2, width: '200px' }} onClick={(e) => setBoatWorkFrom(dataBoatWorkFrom)}>
                                ล้างค่า
                            </Button>
                            <Button variant="contained" color="primary" sx={{ width: '200px' }} onClick={() => handleSearchWork(boatWorkFrom, token)}>
                                ค้นหา
                            </Button>
                        </Box>
                    </Card>
                    <Card sx={{ height: 'auto', overflow: '-moz-hidden-unscrollable', }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                รายการการทำงานของเรือ
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>ลำดับ</StyledTableCell>
                                            <StyledTableCell>หมายเลขบุ๊คกิ้ง</StyledTableCell>
                                            <StyledTableCell>ชื่อเรือ</StyledTableCell>
                                            <StyledTableCell>ผู้โดยสาร</StyledTableCell>
                                            <StyledTableCell>ผู้โดยสารทั้งหมด</StyledTableCell>
                                            <StyledTableCell>วันที่เริ่มทำงาน</StyledTableCell>
                                            <StyledTableCell>เวลา</StyledTableCell>
                                            <StyledTableCell>ผู้ดูแล</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boatWork
                                            .slice(workPage * workRowsPerPage, workPage * workRowsPerPage + workRowsPerPage)
                                            .map((row, index) => (
                                                <StyledTableRow key={row.id}>
                                                    <StyledTableCell>{workPage * workRowsPerPage + index + 1}</StyledTableCell>
                                                    <StyledTableCell>{row.booking_id}</StyledTableCell>
                                                    <StyledTableCell>{row.boatname}</StyledTableCell>
                                                    <StyledTableCell>
                                                        <Typography>
                                                            ผู้ใหญ่ :{row.total_adults} คน
                                                        </Typography>
                                                        <Typography>
                                                            เด็ก   :{row.total_children} คน
                                                        </Typography>
                                                    </StyledTableCell>
                                                    <StyledTableCell>{row.total_adults + row.total_children}  คน</StyledTableCell>
                                                    <StyledTableCell>{row.date}</StyledTableCell>
                                                    <StyledTableCell>{row.time}</StyledTableCell>
                                                    <StyledTableCell>
                                                        <Typography>
                                                            พนักงานขับ :{row.driverName}
                                                        </Typography>
                                                        <Typography>
                                                            ผู้ช่วย     :{row.butlerName}
                                                        </Typography>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25]}
                                component="div"
                                count={boatWork.length}
                                rowsPerPage={workRowsPerPage}
                                page={workPage}
                                onPageChange={handleChangeWorkPage}
                                onRowsPerPageChange={handleChangeWorkRowsPerPage}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box sx={{ bgcolor: '#ECECEC' }}>
            <ResponsiveAppBar />
            <Box sx={{ flexGrow: 1, margin: 10, marginBottom: 2, marginTop: 1 }}>
                <Typography variant="h4" gutterBottom>
                    Boat Management
                </Typography>
                {renderContent()}
            </Box>
        </Box>
    );
};

export default ShipInventoryDashboard;